import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type { CommunityFeedSelectableAudienceV2Dto, MessageCreateRequest } from "@/api/types";
import {
  convertToCommunityFeedAudienceItemType,
  MAX_LENGTH_RESIDENT_POST_CONTENT,
  MAX_LENGTH_RESIDENT_POST_TITLE,
} from "@/components/CommunityFeed/helpers";
import { ResidentAudienceGroupOption } from "@/components/CommunityFeed/ResidentAudienceGroupOption";
import type { CommunityFeedAudienceItem, ResidentCreatePostModalFormValues } from "@/components/CommunityFeed/types";
import { EntityPreview } from "@/components/EntityPreview/EntityPreview";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { ContentBlock } from "@/design-system/ContentBlock";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { Spread } from "@/design-system/Spread";
import type { MessageRouteStates } from "@/helpers/community-feed";
import { buildDeepLinkUrl, type SupportedDeepLinkEntityType } from "@/helpers/deep-link";
import { createRequiredStringRule } from "@/helpers/rules";
import { useProject } from "@/hooks/Network/useProject";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

const FORM_ID = "resident-share-to-community-feed-modal-form";

type ResidentShareToCommunityFeedModalProps = {
  entityType: SupportedDeepLinkEntityType;
  entityId: string;
  otherIds?: Record<string, string>;
} & ModalBaseProps;

type ResidentShareToCommunityFeedModalFormValues = Omit<
  ResidentCreatePostModalFormValues,
  "images" | "documents" | "videos"
>;

export function ResidentShareToCommunityFeedModal({
  isOpened,
  onOpenChange,
  entityType,
  entityId,
  otherIds,
}: ResidentShareToCommunityFeedModalProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const project = useProject();
  const { appBrandingConfig } = useProjectAppBrandingConfig();
  const communityFeedQueries = useCommunityFeedQueries();

  const query: ApiQueryParams<"getCommunityFeedAudienceV2"> = { Type: "post" };
  const {
    data: postAudience,
    isLoading: isLoadingAudience,
    error: errorLoadingAudience,
  } = useQuery(communityFeedQueries.audience({ query }));
  const addMessageMutation = communityFeedMutations.useAddMessage();

  const groups = useMemo(() => {
    if (!postAudience) {
      return [];
    }

    return [
      ...postAudience.interestGroups
        .filter((x) => x.id !== sessionUser.marketPlaceGroupId)
        .map(convertToCommunityFeedAudienceItemType("group")),
      ...postAudience.realEstateGroups.map(convertToCommunityFeedAudienceItemType("group")),
      ...(postAudience.project ? [convertToCommunityFeedAudienceItemType("project")(postAudience.project)] : []),
      ...postAudience.projectConnections.map(convertToCommunityFeedAudienceItemType("projectConnection")),
    ];
  }, [postAudience, sessionUser.marketPlaceGroupId]);

  const form = useForm<ResidentShareToCommunityFeedModalFormValues>({
    defaultValues: {
      title: "",
      content: "",
      group: undefined,
      audience: undefined,
    },
  });

  useEffect(() => {
    if (isOpened) {
      form.reset();
    }
  }, [isOpened, form]);

  const handleSubmit = (formValues: ResidentShareToCommunityFeedModalFormValues) => {
    let projectConnectionId: string | undefined;
    if (formValues.group?.type === "projectConnection") {
      projectConnectionId = formValues.group.value.id;
    } else if (formValues.audience?.type === "projectConnection") {
      projectConnectionId = formValues.audience.projectConnection?.id;
    }

    let deepLinkUrl: string;
    if (entityType === "document") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          folderId: otherIds?.folderId || "",
        },
      });
    } else if (entityType === "group") {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
          groupTypeId: otherIds?.groupTypeId || "",
        },
      });
    } else {
      deepLinkUrl = buildDeepLinkUrl({
        baseUrl: appBrandingConfig.deepLink,
        payload: {
          entityType,
          entityId,
          projectId: project.id,
        },
      });
    }

    // Should always be of type `inform`
    const data: MessageCreateRequest = {
      type: "undefined",
      messageLabel: "inform",
      groupId: formValues.group?.type === "group" ? formValues.group.value.id : undefined,
      projectConnectionId,
      title: formValues.title,
      content: formValues.content + " " + deepLinkUrl,
      isHiddenFromAdmins: formValues.hideFromAdmin,
    };

    addMessageMutation.mutate(
      { data },
      {
        onSuccess: (createdMessage) => {
          onOpenChange(false);
          showFlashToast({ variant: "success", message: t("page.message-feed.create.notifications.success") });

          void navigate(
            routes.communityFeed.list({
              slug,
            }),
            {
              state: { newMessageId: createdMessage.id } satisfies MessageRouteStates["list"],
            },
          );
        },
      },
    );
  };

  const currGroup = useWatch({ control: form.control, name: "group" });
  const currAudience = useWatch({ control: form.control, name: "audience" });

  const isHideFromAdminOptionVisible =
    currAudience?.type !== "projectConnection" &&
    currGroup?.type !== "projectConnection" &&
    sessionUser.allowHidePostForAdmin;

  return (
    <Modal.Root
      data-testid="resident-create-post-modal"
      height={640}
      title={t("page.message-feed.resident-create-post.title.inform")}
      description={t("page.message-feed.resident-create-post.subtitle.inform")}
      isPending={isLoadingAudience}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} data-testid="resident-create-post-form" formMethods={form} onSubmit={handleSubmit}>
          <>
            {!isLoadingAudience && errorLoadingAudience && (
              <Box padding={{ y: "xl" }}>
                <Notice variant="danger" message={t("page.message-feed.resident-create-post.loading.error")} />
              </Box>
            )}
            {!isLoadingAudience && !errorLoadingAudience && (
              <Form.Body>
                <FormField label={t("page.message-feed.resident-create-post.form.audience.title")} required>
                  <FormSelect<ResidentShareToCommunityFeedModalFormValues, CommunityFeedAudienceItem>
                    name="group"
                    placeholder={t("page.message-feed.resident-create-post.form.audience.placeholder")}
                    items={groups}
                    keySelector={(x) => x.type + x.value.id}
                    renderSelected={(x) => <ResidentAudienceGroupOption group={x} isPreview />}
                    renderOption={(x) => <ResidentAudienceGroupOption group={x} />}
                    groupSelector={(x) => {
                      if (x.type === "group") {
                        switch (x.value.type) {
                          case "helpCategory":
                            return t(
                              "page.message-feed.resident-create-post.form.audience.option-groups.help-categories",
                            );
                          case "interest":
                            return t(
                              "page.message-feed.resident-create-post.form.audience.option-groups.interest-groups",
                            );
                          default:
                            return t("page.message-feed.resident-create-post.form.audience.option-groups.real-estate");
                        }
                      } else {
                        if (x.type === "project") {
                          return t("page.message-feed.resident-create-post.form.audience.option-groups.message-all");
                        }

                        return t("page.message-feed.resident-create-post.form.audience.option-groups.connections");
                      }
                    }}
                    onChange={(x) => {
                      if (x?.type === "group") {
                        const defaultAudience = x.value.selectableAudience.find(
                          (a) => a.type === x.value.defaultPostingLevel,
                        );

                        form.setValue("audience", defaultAudience);
                        if (defaultAudience?.type === "projectConnection") {
                          form.setValue("hideFromAdmin", false);
                        }
                      }

                      if (x?.type === "projectConnection") {
                        form.setValue("hideFromAdmin", false);
                      }
                    }}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: t("page.message-feed.resident-create-post.form.audience.title"),
                      }),
                    }}
                  />
                  {currGroup?.type === "group" &&
                  (currGroup.value.selectableAudience.length > 1 ||
                    (currGroup.value.selectableAudience.length === 1 &&
                      currGroup.value.selectableAudience[0].type === "projectConnection")) ? (
                    <section className="flex flex-col gap-space-xs pt-space-sm">
                      <span className="text-caption">{t("page.admin-create-post.form.audience.audience.label")}</span>
                      <FormSelect<ResidentShareToCommunityFeedModalFormValues, CommunityFeedSelectableAudienceV2Dto>
                        name="audience"
                        items={currGroup.value.selectableAudience}
                        onChange={(x) => {
                          form.setValue("audience", x);

                          if (x?.type === "projectConnection") {
                            form.setValue("hideFromAdmin", false);
                          }
                        }}
                        keySelector={(x) => x.type + (x.project?.id || x.projectConnection?.id)}
                        renderOption={(x) => (
                          <Spread>
                            <span>{x.project?.name || x.projectConnection?.name || ""}</span>
                            <span className="text-caption text-text-secondary italic">
                              {t("page.message-feed.resident-create-post.form.audience.group.members", {
                                count: x.project?.audienceCount || x.projectConnection?.audienceCount || 0,
                              })}
                            </span>
                          </Spread>
                        )}
                        groupSelector={(x) => {
                          if (x.type === "project") {
                            return t("page.message-feed.resident-create-post.form.audience.option-groups.message-all");
                          }

                          return t("page.message-feed.resident-create-post.form.audience.option-groups.connections");
                        }}
                        disabled={currGroup.value.selectableAudience.length === 1}
                      />
                    </section>
                  ) : null}
                </FormField>
                <FormField label={t("page.message-feed.resident-create-post.form.title.title")} required>
                  <FormInput<ResidentShareToCommunityFeedModalFormValues>
                    name="title"
                    placeholder={t("page.message-feed.resident-create-post.form.title.placeholder.inform")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(
                          t,
                          "page.message-feed.resident-create-post.form.title.title",
                        ),
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH_RESIDENT_POST_TITLE }),
                        value: MAX_LENGTH_RESIDENT_POST_TITLE,
                      },
                    }}
                  />
                </FormField>
                <FormField label={t("page.message-feed.resident-create-post.form.content.title")}>
                  <FormTextArea<ResidentShareToCommunityFeedModalFormValues>
                    name="content"
                    placeholder={t("page.message-feed.resident-create-post.form.content.placeholder.inform")}
                    rules={{
                      maxLength: {
                        message: t("components.form.error.max-length", { length: MAX_LENGTH_RESIDENT_POST_CONTENT }),
                        value: MAX_LENGTH_RESIDENT_POST_CONTENT,
                      },
                    }}
                  />
                </FormField>
                <ContentBlock width="flow" align="left">
                  <EntityPreview entityId={entityId} entityType={entityType} otherIds={otherIds || {}} readOnly />
                </ContentBlock>
                {isHideFromAdminOptionVisible && (
                  <FormCheckbox<ResidentShareToCommunityFeedModalFormValues>
                    name="hideFromAdmin"
                    label={t("page.message-feed.resident-create-post.form.hide-from-admins.title")}
                  />
                )}
              </Form.Body>
            )}
          </>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions isSticky>
        <Modal.Close>
          <Button
            variant="outline"
            tone="primary"
            onClick={() => onOpenChange(false)}
            disabled={addMessageMutation.isPending}
          >
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button form={FORM_ID} variant="filled" type="submit" isLoading={addMessageMutation.isPending}>
          {t("common.action.post")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
